import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import { useTherapyCandidatesForTimeSlot, useSetTimeSlotTherapy } from "../../../stores/Appointment/therapyMethod.hooks";

interface TherapyMethodPickerDialogProps {
  open: boolean;
  timeSlotId: string | null;
  onClose: () => void;
  /** Notified after a successful change so hosts can patch local state. */
  onApplied?: (newTherapy: { id: string; abbreviation: string }) => void;
}

/**
 * MD-302 — pick a non-default therapy method for one timeslot.
 *
 * An umbrella prescription term (e.g. "Wärmetherapie") resolves to several
 * billable methods (Heißluft / Warmpackung / Heiße Rolle); by default the slot
 * carries the highest-priority one the therapist is credentialed for. This
 * dialog lets the user override that pick with any other permitted candidate.
 * A manual pick is sticky: it survives moves and therapist changes while it
 * stays permitted. "Automatisch" returns the slot to the default rule.
 */
const TherapyMethodPickerDialog: React.FC<TherapyMethodPickerDialogProps> = ({
  open,
  timeSlotId,
  onClose,
  onApplied,
}) => {
  const { data, isLoading } = useTherapyCandidatesForTimeSlot(timeSlotId ?? undefined, open);
  const setTherapy = useSetTimeSlotTherapy();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Re-seed the selection whenever the dialog (re)opens on fresh data.
  useEffect(() => {
    if (open) setSelectedId(data?.currentTherapyId ?? null);
  }, [open, data?.currentTherapyId]);

  const candidates = data?.candidates ?? [];
  const hasAlternatives = candidates.length > 1;

  const handleApply = async () => {
    if (!timeSlotId || !selectedId) return;
    const result = await setTherapy.mutateAsync({ timeSlotId, therapyId: selectedId });
    const chosen = result.candidates.find((c) => c.id === result.currentTherapyId);
    if (chosen) onApplied?.({ id: chosen.id, abbreviation: chosen.abbreviation });
    onClose();
  };

  const handleReset = async () => {
    if (!timeSlotId) return;
    const result = await setTherapy.mutateAsync({ timeSlotId, therapyId: null });
    const chosen = result.candidates.find((c) => c.id === result.currentTherapyId);
    if (chosen) onApplied?.({ id: chosen.id, abbreviation: chosen.abbreviation });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Alternative Methoden
        {data?.termLabel ? (
          <Typography variant="body2" color="text.secondary">
            Verordnet: {String(data.termLabel)}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !hasAlternatives ? (
          <Alert severity="info">
            Für dieses Heilmittel gibt es keine alternativen Methoden.
          </Alert>
        ) : (
          <>
            {data?.therapyIsManuallyChosen && (
              <Chip
                size="small"
                color="info"
                label="Methode manuell gewählt"
                sx={{ mb: 1.5 }}
              />
            )}
            <RadioGroup
              value={selectedId ?? ""}
              onChange={(_e, value) => setSelectedId(value)}
            >
              {candidates.map((c) => (
                <Tooltip
                  key={c.id}
                  title={
                    !c.isCredentialed
                      ? "Der zugewiesene Therapeut hat dieses Heilmittel nicht."
                      : ""
                  }
                  placement="left"
                >
                  <FormControlLabel
                    value={c.id}
                    disabled={!c.isCredentialed}
                    control={<Radio size="small" />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                        <Typography component="span" fontWeight={600}>
                          {c.abbreviation}
                        </Typography>
                        {c.description ? (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {String(c.description)}
                          </Typography>
                        ) : null}
                        {c.duration ? (
                          <Typography component="span" variant="caption" color="text.secondary">
                            ({Number(c.duration)} Min.)
                          </Typography>
                        ) : null}
                        {c.isDefault && (
                          <Chip size="small" variant="outlined" label="Standard" />
                        )}
                      </Box>
                    }
                  />
                </Tooltip>
              ))}
            </RadioGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        {data?.therapyIsManuallyChosen && (
          <Button onClick={handleReset} disabled={setTherapy.isPending}>
            Automatisch
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={
            !hasAlternatives ||
            !selectedId ||
            selectedId === data?.currentTherapyId ||
            setTherapy.isPending
          }
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TherapyMethodPickerDialog;
